import React, { useState, useEffect } from 'react';
import invoiceService from 'services/invoiceService';
import Swal from 'sweetalert2';
import 'styles/provider.css';

export const DashboardView = () => {
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({
    totalFacturado: 0,
    pendientes: 0,
    aprobadas: 0,
    proximoPago: null
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);

      // Cargar estadísticas
      const statsResponse = await invoiceService.getInvoiceStats();

      if (!statsResponse.error) {
        const statsData = statsResponse.data.data;
        setStats({
          totalFacturado: statsData.monto_total || 0,
          pendientes: Number(statsData.recibidas || 0) + Number(statsData.en_revision || 0),
          aprobadas: statsData.aprobadas || 0,
          proximoPago: null
        });
      }

      // Cargar facturas recientes (últimas 5)
      const invoicesResponse = await invoiceService.getInvoices({
        page: 1,
        limit: 5,
        orderBy: 'created_at',
        orderDir: 'DESC'
      });

      if (!invoicesResponse.error) {
        const invoicesData = invoicesResponse.data.data || [];
        setInvoices(invoicesData);

        // Buscar próximo pago
        const proximoPago = invoicesData.find(
          inv => inv.estatus === 'aprobada' && inv.fecha_pago_programada
        );

        if (proximoPago) {
          setStats(prev => ({ ...prev, proximoPago }));
        }
      }

    } catch (error) {
      console.error('Error al cargar dashboard:', error);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: 'No se pudo cargar la información del dashboard',
        timer: 3000,
        showConfirmButton: false
      });
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-MX', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const getStatusMessage = (status) => {
    const messages = {
      'aprobada': 'Factura Aprobada',
      'en_revision': 'Factura En Revisión',
      'recibida': 'Factura Recibida',
      'pagada': 'Pago Realizado',
      'rechazada': 'Factura Rechazada'
    };
    return messages[status] || status;
  };

  const getStatusDescription = (invoice) => {
    const descriptions = {
      'aprobada': `La factura por ${formatCurrency(invoice.total)} ha sido aprobada y programada para pago`,
      'en_revision': 'Su factura está siendo revisada por el departamento de contabilidad',
      'recibida': 'Su factura ha sido recibida y está en proceso de validación',
      'pagada': `Se ha procesado el pago de ${formatCurrency(invoice.total)}`,
      'rechazada': 'La factura ha sido rechazada, por favor revise los comentarios'
    };
    return descriptions[invoice.estatus] || invoice.descripcion;
  };

  const getTimeAgo = (dateString, index) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Hoy';
    if (diffDays === 1) return 'Ayer';
    if (diffDays < 7) return `Hace ${diffDays} días`;
    return formatDate(dateString);
  };

  if (loading) {
    return (
      <div className="dashboard-view">
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Cargando información...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-view">
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-label">Total Facturado</div>
          <div className="stat-value">{formatCurrency(stats.totalFacturado)}</div>
          <div className="stat-change positive">Todas las facturas</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Facturas Pendientes</div>
          <div className="stat-value">{stats.pendientes}</div>
          <div className="stat-change">En proceso de revisión</div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Próximo Pago</div>
          <div className="stat-value">
            {stats.proximoPago ? formatCurrency(stats.proximoPago.total) : 'N/A'}
          </div>
          <div className="stat-change">
            {stats.proximoPago
              ? formatDate(stats.proximoPago.fecha_pago_programada)
              : 'Sin pagos programados'}
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-label">Facturas Aprobadas</div>
          <div className="stat-value">{stats.aprobadas}</div>
          <div className="stat-change positive">Listas para pago</div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">Actividad Reciente</div>
        <div className="timeline">
          {invoices.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📋</div>
              <div className="empty-text">No hay actividad reciente</div>
            </div>
          ) : (
            invoices.slice(0, 5).map((invoice, index) => (
              <div key={invoice.id} className="timeline-item">
                <div className="timeline-marker"></div>
                <div className="timeline-content">
                  <div className="timeline-date">
                    {getTimeAgo(invoice.created_at, index)}
                  </div>
                  <div className="timeline-title">
                    {invoice.folio} - {getStatusMessage(invoice.estatus)}
                  </div>
                  <p className="timeline-description">
                    {getStatusDescription(invoice)}
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};
